'use client';

import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { useEffect, useState } from 'react';

import { Styles, css } from '@flipster/styled-system/css';

import { contentfulImageUrl } from '@web/app/[locale]/campaign/[slug]/_sections/utils';

export const checkLottie = (url: string) => {
  return url.endsWith('.lottie');
};

interface Props {
  url?: string | null;
  alt?: string;
  css?: Styles;
}

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

async function fetchWithRetry(url: string, init?: RequestInit, retries = 3, backoffMs = 500) {
  let lastErr: unknown;
  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      const res = await fetch(url, init);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res;
    } catch (e) {
      lastErr = e;
      if (attempt < retries - 1) {
        await delay(backoffMs * Math.pow(2, attempt)); // 0.5s, 1s, 2s ...
      }
    }
  }
  throw lastErr ?? new Error('fetch failed');
}

export const ImageOrLottie = ({ url, alt, css: cssProps }: Props) => {
  const [blobUrl, setBlobUrl] = useState<string>();
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!url || !checkLottie(url)) return;

    let cancelled = false;
    let objectUrl: string | null = null;

    setBlobUrl(undefined);
    setFailed(false);

    (async () => {
      try {
        const res = await fetchWithRetry(url, undefined, 3, 500);
        const blob = await res.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url]);

  if (!url) return null;

  if (checkLottie(url)) {
    if (blobUrl) return <DotLottieReact src={blobUrl} autoplay loop className={css(cssProps)} />;
    if (failed) return null; // 실패 시
    return null;
  }

  return <img src={contentfulImageUrl(url)} alt={alt} className={css(cssProps)} />;
};
